// Pure helpers for the itemized cost + receipt tracker (Inspection Requests and
// Pre-Market Improvements). No React, no Firestore. Costs are plain number
// strings, optionally with up to 2 decimals for cents ("1186" or "1186.45").
// ESM named exports so the production build resolves the named imports cleanly.

function _num(cost) {
  const n = Number(String(cost == null ? "" : cost).replace(/[^\d.]/g, ""));
  return Number.isNaN(n) ? 0 : n;
}

export function totalCost(items) {
  return (Array.isArray(items) ? items : []).reduce(
    (sum, it) => sum + _num(it && it.cost),
    0,
  );
}

export function owedToYou(items) {
  return (Array.isArray(items) ? items : []).reduce((sum, it) => {
    if (it && it.paidBy === "you" && !it.reimbursed) return sum + _num(it.cost);
    return sum;
  }, 0);
}

// A cost is fully SETTLED (nobody is still owed for it) when:
//   • the client / seller / other paid it themselves, or
//   • the agent fronted it AND has been reimbursed.
// An agent-fronted cost that has NOT been reimbursed is still owed — to the
// agent, at closing — so it counts toward owed-at-closing, not already-paid.
const SETTLED_PAYERS = new Set(["client", "seller", "other"]);

export function alreadyPaid(items) {
  return (Array.isArray(items) ? items : []).reduce((sum, it) => {
    if (!it) return sum;
    const settled =
      SETTLED_PAYERS.has(it.paidBy) || (it.paidBy === "you" && it.reimbursed);
    return settled ? sum + _num(it.cost) : sum;
  }, 0);
}

// Owed at closing = "Pay at Closing" items PLUS anything the agent fronted that
// hasn't been reimbursed yet (the agent is made whole out of closing proceeds).
export function owedAtClosing(items) {
  return (Array.isArray(items) ? items : []).reduce((sum, it) => {
    if (!it) return sum;
    const owed =
      it.paidBy === "closing" || (it.paidBy === "you" && !it.reimbursed);
    return owed ? sum + _num(it.cost) : sum;
  }, 0);
}

export function blankItem(id) {
  return {
    id: String(id),
    description: "",
    vendor: "",
    done: false,
    cost: "",
    paidBy: "",
    reimbursed: false,
    receiptUrl: "",
    receiptName: "",
    receiptFileId: "",
  };
}

export function addItem(items, id) {
  return [...(Array.isArray(items) ? items : []), blankItem(id)];
}

export function updateItem(items, id, patch) {
  return (Array.isArray(items) ? items : []).map((it) =>
    it.id === id ? { ...it, ...patch } : it,
  );
}

export function removeItem(items, id) {
  return (Array.isArray(items) ? items : []).filter((it) => it.id !== id);
}
